import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import { 
  FaGraduationCap, FaBook, FaMoneyBill, FaClock, 
  FaCheckCircle, FaUpload, FaCalendar, FaCamera,
  FaTimes, FaStar, FaLanguage, FaUserGraduate,
  FaUser, FaAward, FaCheck, FaArrowLeft, FaArrowRight,
  FaRegCheckCircle, FaSpinner
} from 'react-icons/fa';
import Navbar from '../layout/Navbar';

const TutorProfileSetup = () => {
  const { user, userData } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    subjects: [],
    hourlyRate: 25,
    education: '',
    experience: 0,
    bio: '',
    teachingStyle: '',
    availability: [],
    profileImage: '',
    qualifications: [],
    languages: ['English'],
    levels: []
  });
  const [imagePreview, setImagePreview] = useState('');
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [errors, setErrors] = useState({});
  const [animate, setAnimate] = useState(true);

  const subjects = [
    'Mathematics', 'Physics', 'Chemistry', 'Biology',
    'English', 'French', 'History', 'Geography',
    'Computer Science', 'Economics', 'Accounting',
    'Further Mathematics', 'Literature', 'Business Studies'
  ];

  const educationLevels = [
    'High School Graduate',
    'Undergraduate Student',
    'Bachelor\'s Degree',
    'Master\'s Degree',
    'PhD',
    'Professional Certification'
  ];

  const teachingLevels = [
    'Primary', 'Form 1-2', 'Form 3-5', 'Lower Sixth',
    'Upper Sixth', 'University', 'Professional'
  ];

  const languages = ['English', 'French', 'Spanish', 'German', 'Chinese', 'Arabic'];

  useEffect(() => {
    const defaultImage = 'https://via.placeholder.com/150x150/667eea/ffffff?text=TU';
    setImagePreview(userData?.profileImage || defaultImage);
    setFormData(prev => ({ 
      ...prev, 
      profileImage: userData?.profileImage || defaultImage 
    }));
    
    // Trigger entrance animation
    setAnimate(true);
    const timer = setTimeout(() => setAnimate(false), 1000);
    return () => clearTimeout(timer);
  }, [userData]);

  useEffect(() => {
    validateCurrentStep();
  }, [formData, step]);

  const validateCurrentStep = () => {
    const newErrors = {};
    
    if (step === 1) {
      if (!formData.education.trim()) newErrors.education = 'Education level is required';
      if (formData.experience < 0) newErrors.experience = 'Experience cannot be negative';
    }
    
    if (step === 2) {
      if (formData.subjects.length === 0) newErrors.subjects = 'Select at least one subject';
      if (formData.levels.length === 0) newErrors.levels = 'Select at least one teaching level';
      if (formData.hourlyRate < 5) newErrors.hourlyRate = 'Minimum rate is $5/hour';
      if (formData.hourlyRate > 100) newErrors.hourlyRate = 'Maximum rate is $100/hour';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubjectToggle = (subject) => {
    setFormData(prev => ({
      ...prev,
      subjects: prev.subjects.includes(subject)
        ? prev.subjects.filter(s => s !== subject)
        : [...prev.subjects, subject]
    }));
  };

  const handleLevelToggle = (level) => {
    setFormData(prev => ({
      ...prev,
      levels: prev.levels.includes(level)
        ? prev.levels.filter(l => l !== level)
        : [...prev.levels, level]
    }));
  };

  const handleLanguageToggle = (language) => {
    setFormData(prev => ({
      ...prev,
      languages: prev.languages.includes(language)
        ? prev.languages.filter(l => l !== language)
        : [...prev.languages, language]
    }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file (JPEG, PNG, etc.)');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      alert('Image size should be less than 2MB');
      return;
    }

    setUploading(true);

    try {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);

      const imageRef = ref(storage, `profile-images/${user.uid}/${Date.now()}_${file.name}`);
      const snapshot = await uploadBytes(imageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      setFormData(prev => ({ ...prev, profileImage: downloadURL }));
      await updateDoc(doc(db, 'users', user.uid), {
        profileImage: downloadURL
      });
      
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const addQualification = () => {
    const qualification = prompt('Enter your qualification (e.g., BSc in Mathematics):');
    if (qualification && qualification.trim()) {
      setFormData(prev => ({
        ...prev,
        qualifications: [...prev.qualifications, qualification.trim()]
      }));
    }
  };

  const removeQualification = (index) => {
    setFormData(prev => ({
      ...prev,
      qualifications: prev.qualifications.filter((_, i) => i !== index)
    }));
  };

  const addAvailabilitySlot = () => {
    const day = prompt('Enter day (e.g., Monday):');
    const time = prompt('Enter time slot (e.g., 9:00 AM - 12:00 PM):');
    
    if (day && time) {
      setFormData(prev => ({
        ...prev,
        availability: [...prev.availability, { day, time }]
      }));
    }
  };

  const removeAvailabilitySlot = (index) => {
    setFormData(prev => ({
      ...prev,
      availability: prev.availability.filter((_, i) => i !== index)
    }));
  };

  const handleNextStep = () => {
    if (validateCurrentStep()) {
      setAnimate(true);
      setTimeout(() => {
        setStep(step + 1);
        setAnimate(false);
      }, 300);
    }
  };

  const handlePrevStep = () => {
    setAnimate(true);
    setTimeout(() => {
      setStep(step - 1);
      setAnimate(false);
    }, 300);
  };

  const handleSubmit = async () => {
    if (!validateCurrentStep()) {
      alert('Please fix the errors before submitting');
      return;
    }

    setLoading(true);
    setSuccess('');

    try {
      const profileImageToSave = formData.profileImage || userData?.profileImage || 'https://via.placeholder.com/150';

      const tutorData = {
        subjects: formData.subjects,
        hourlyRate: parseFloat(formData.hourlyRate) || 25,
        education: formData.education,
        experience: parseInt(formData.experience) || 0,
        bio: formData.bio || '',
        teachingStyle: formData.teachingStyle || '',
        availability: formData.availability || [],
        qualifications: formData.qualifications || [],
        languages: formData.languages || ['English'],
        levels: formData.levels || [],
        profileComplete: true,
        status: 'pending',
        rating: 0,
        totalReviews: 0,
        totalSessions: 0,
        updatedAt: new Date(),
        createdAt: new Date()
      };

      const userUpdateData = {
        profileCompleted: true,
        profileImage: profileImageToSave,
        updatedAt: new Date()
      };

      // Update tutor profile
      await updateDoc(doc(db, 'tutors', user.uid), tutorData);
      
      // Update user profile
      await updateDoc(doc(db, 'users', user.uid), userUpdateData);

      setSuccess('Profile setup complete! Your profile is under review.');
      
      setTimeout(() => {
        navigate('/dashboard');
      }, 3000);
    } catch (error) {
      console.error('Error updating tutor profile:', error);
      alert('Failed to save profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Navbar />
      <div className="profile-setup-page">
        {/* Setup Header */}
        <div className="setup-hero">
          <div className="container">
            <div className="setup-header">
              <h1>Complete Your Tutor Profile</h1>
              <p className="setup-subtitle">Setup your profile to start teaching</p>
              
              <div className="progress-steps" style={{ textAlign: 'center', marginTop: 8 }}>
                {['Basic Info', 'Teaching Details', 'Availability'].map((title, i) => (
                  <span key={i} className={`progress-item ${step === i + 1 ? 'active' : ''}`} style={{ margin: '0 8px', fontWeight: step === i + 1 ? 700 : 500 }}>
                    {title}
                    {i < 2 && <span style={{ margin: '0 8px', color: '#cbd5e1' }}>•</span>}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="container">
          <div className="setup-content">
            {/* Step 1: Basic Information */}
            {step === 1 && (
              <div className={`setup-step ${animate ? 'animate-fade-in' : ''}`}>
                <div className="step-header">
                  <h2><FaUser /> Basic Information</h2>
                  <p className="step-description">Let's start with your basic details and background</p>
                </div>
                
                <div className="profile-image-section">
                  <div className="image-upload-card">
                    <div className="image-preview">
                      {imagePreview ? (
                        <img src={imagePreview} alt="Profile preview" />
                      ) : (
                        <div className="image-placeholder">
                          <FaCamera />
                          <span>Add Photo</span>
                        </div>
                      )}
                      {uploading && (
                        <div className="uploading-overlay">
                          <FaSpinner className="spinner" />
                          <span>Uploading...</span>
                        </div>
                      )}
                    </div>
                    <div className="image-actions">
                      <label className="btn btn-outline">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          disabled={uploading}
                          hidden
                        />
                        <FaUpload /> Upload Photo
                      </label>
                      <p className="help-text">JPEG or PNG, max 2MB</p>
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <div className="form-row">
                    <div className="form-group">
                      <label>
                        <FaGraduationCap /> Education Level *
                      </label>
                      <select
                        value={formData.education}
                        onChange={(e) => setFormData({...formData, education: e.target.value})}
                        className={errors.education ? 'error' : ''}
                      >
                        <option value="">Select your highest education</option>
                        {educationLevels.map(level => (
                          <option key={level} value={level}>{level}</option>
                        ))}
                      </select>
                      {errors.education && (
                        <span className="error-message">{errors.education}</span>
                      )}
                    </div>

                    <div className="form-group">
                      <label>
                        <FaAward /> Years of Experience *
                      </label>
                      <input
                        type="number"
                        value={formData.experience}
                        onChange={(e) => setFormData({...formData, experience: parseInt(e.target.value) || 0})}
                        placeholder="e.g., 3"
                        min="0"
                        max="50"
                        className={errors.experience ? 'error' : ''}
                      />
                      {errors.experience && (
                        <span className="error-message">{errors.experience}</span>
                      )}
                    </div>
                  </div>

                  <div className="form-group">
                    <label>
                      <FaUserGraduate /> Teaching Style (Optional)
                    </label>
                    <textarea
                      value={formData.teachingStyle}
                      onChange={(e) => setFormData({...formData, teachingStyle: e.target.value})}
                      placeholder="Describe your teaching methodology, approach, and what makes your tutoring unique..."
                      rows="4"
                      className="style-textarea"
                    />
                  </div>

                  <div className="form-group">
                    <label>
                      <FaUser /> Biography (Optional)
                    </label>
                    <textarea
                      value={formData.bio}
                      onChange={(e) => setFormData({...formData, bio: e.target.value})}
                      placeholder="Tell students about yourself, your background, and why you're passionate about teaching..."
                      rows="4"
                      className="bio-textarea"
                    />
                  </div>

                  <div className="qualifications-section">
                    <label>
                      <FaGraduationCap /> Qualifications & Certifications
                    </label>
                    <div className="qualifications-list">
                      {formData.qualifications.map((qual, index) => (
                        <div key={index} className="qualification-tag">
                          <FaCheckCircle />
                          <span>{qual}</span>
                          <button
                            type="button"
                            className="remove-btn"
                            onClick={() => removeQualification(index)}
                          >
                            <FaTimes />
                          </button>
                        </div>
                      ))}
                    </div>
                    <button
                      type="button"
                      className="btn btn-ghost"
                      onClick={addQualification}
                    >
                      + Add Qualification
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Teaching Details */}
            {step === 2 && (
              <div className={`setup-step ${animate ? 'animate-fade-in' : ''}`}>
                <div className="step-header">
                  <h2><FaBook /> Teaching Details</h2>
                  <p className="step-description">Define what subjects you teach and at what levels</p>
                </div>
                
                <div className="form-section">
                  <div className="form-group">
                    <label>
                      <FaBook /> Subjects You Teach *
                    </label>
                    <div className="subjects-selector">
                      {subjects.map(subject => (
                        <div
                          key={subject}
                          className={`subject-option ${formData.subjects.includes(subject) ? 'selected' : ''}`}
                          onClick={() => handleSubjectToggle(subject)}
                        >
                          <span className="subject-name">{subject}</span>
                          {formData.subjects.includes(subject) && (
                            <FaCheckCircle className="check-icon" />
                          )}
                        </div>
                      ))}
                    </div>
                    {errors.subjects && (
                      <span className="error-message">{errors.subjects}</span>
                    )}
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>
                        <FaUserGraduate /> Teaching Levels *
                      </label>
                      <div className="levels-selector">
                        {teachingLevels.map(level => (
                          <div
                            key={level}
                            className={`level-option ${formData.levels.includes(level) ? 'selected' : ''}`}
                            onClick={() => handleLevelToggle(level)}
                          >
                            <span>{level}</span>
                            {formData.levels.includes(level) && (
                              <FaCheckCircle className="check-icon" />
                            )}
                          </div>
                        ))}
                      </div>
                      {errors.levels && (
                        <span className="error-message">{errors.levels}</span>
                      )}
                    </div>

                    <div className="form-group">
                      <label>
                        <FaLanguage /> Languages You Speak
                      </label>
                      <div className="languages-selector">
                        {languages.map(language => (
                          <div
                            key={language}
                            className={`language-option ${formData.languages.includes(language) ? 'selected' : ''}`}
                            onClick={() => handleLanguageToggle(language)}
                          >
                            <span>{language}</span>
                            {formData.languages.includes(language) && (
                              <FaCheckCircle className="check-icon" />
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>
                      <FaMoneyBill /> Hourly Rate (USD) *
                    </label>
                    <div className="rate-selector">
                      <div className="rate-display">
                        <span className="currency">$</span>
                        <input
                          type="number"
                          value={formData.hourlyRate}
                          onChange={(e) => setFormData({...formData, hourlyRate: Math.min(100, Math.max(5, parseFloat(e.target.value) || 25))})}
                          min="5"
                          max="100"
                          step="5"
                          className={errors.hourlyRate ? 'error' : ''}
                        />
                        <span className="period">/ hour</span>
                      </div>
                      {errors.hourlyRate && (
                        <span className="error-message">{errors.hourlyRate}</span>
                      )}
                      <div className="rate-slider">
                        <input
                          type="range"
                          min="5"
                          max="100"
                          step="5"
                          value={formData.hourlyRate}
                          onChange={(e) => setFormData({...formData, hourlyRate: parseInt(e.target.value)})}
                          className="slider"
                        />
                        <div className="slider-labels">
                          <span>$5</span>
                          <span>$25</span>
                          <span>$50</span>
                          <span>$75</span>
                          <span>$100</span>
                        </div>
                      </div>
                      <p className="help-text">Recommended for Cameroon: $15 - $40 per hour</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Availability */}
            {step === 3 && (
              <div className={`setup-step ${animate ? 'animate-fade-in' : ''}`}>
                <div className="step-header">
                  <h2><FaClock /> Availability</h2>
                  <p className="step-description">Set your available time slots for tutoring sessions</p>
                </div>
                
                <div className="form-section">
                  <div className="availability-section">
                    <div className="section-header">
                      <h3>Weekly Schedule</h3>
                      <button
                        type="button"
                        className="btn btn-outline"
                        onClick={addAvailabilitySlot}
                      >
                        <FaCalendar /> Add Time Slot
                      </button>
                    </div>
                    
                    {formData.availability.length > 0 ? (
                      <div className="availability-list">
                        {formData.availability.map((slot, index) => (
                          <div key={index} className="availability-card">
                            <div className="slot-info">
                              <FaCalendar className="slot-icon" />
                              <div className="slot-details">
                                <strong>{slot.day}</strong>
                                <span>{slot.time}</span>
                              </div>
                            </div>
                            <button
                              type="button"
                              className="btn-icon"
                              onClick={() => removeAvailabilitySlot(index)}
                              aria-label="Remove time slot"
                            >
                              <FaTimes />
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="empty-state">
                        <FaCalendar className="empty-icon" />
                        <h4>No Availability Set</h4>
                        <p>You can add your availability now or later from your dashboard</p>
                      </div>
                    )}
                    
                    <div className="availability-info">
                      <h4><FaStar /> Important Notes</h4>
                      <ul>
                        <li>Students can only book during your available time slots</li>
                        <li>Update availability anytime from your dashboard</li>
                        <li>Recommended: 24-hour cancellation policy</li>
                        <li>Consider time zone differences</li>
                        <li>Minimum 1-hour sessions recommended</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Success Message */}
            {success && (
              <div className="success-message animate-fade-in">
                <FaCheckCircle className="success-icon" />
                <div className="success-content">
                  <h3>Profile Setup Complete!</h3>
                  <p>{success}</p>
                  <div className="progress-bar">
                    <div className="progress-fill" style={{ width: '100%' }}></div>
                  </div>
                  <p className="redirect-text">Redirecting to dashboard...</p>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="step-navigation">
              {step > 1 && !success && (
                <button
                  className="btn btn-outline btn-prev"
                  onClick={handlePrevStep}
                  disabled={loading}
                >
                  <FaArrowLeft /> Back
                </button>
              )}
              
              {step < 3 && !success && (
                <button
                  className="btn btn-primary btn-next"
                  onClick={handleNextStep}
                  disabled={loading}
                >
                  Next Step <FaArrowRight />
                </button>
              )}
              
              {step === 3 && !success && (
                <button
                  className="btn btn-success btn-submit"
                  onClick={handleSubmit}
                  disabled={loading || Object.keys(errors).length > 0}
                >
                  {loading ? (
                    <>
                      <FaSpinner className="spinner" /> Saving...
                    </>
                  ) : (
                    <>
                      <FaCheckCircle /> Complete Setup
                    </>
                  )}
                </button>
              )}
              
              {success && (
                <button
                  className="btn btn-primary"
                  onClick={() => navigate('/dashboard')}
                >
                  Go to Dashboard
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TutorProfileSetup;